import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const DoctorDashboard = () => {
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  
  // LAYER 1: Initialize as empty array
  const [appointments, setAppointments] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [stats, setStats] = useState({
    totalAppointments: 0,
    totalPatients: 0,
    totalEarnings: 0,
    pendingAppointments: 0
  });

  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTimeStart, setScheduleTimeStart] = useState('');
  const [scheduleTimeEnd, setScheduleTimeEnd] = useState('');
  const [mySlots, setMySlots] = useState([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '', phone: '', specialty: '', fee: '',
    qualifications: '', experience: '', image: ''
  });

  useEffect(() => {
    const userString = localStorage.getItem('DDoctorUser');
    if (!userString) {
      navigate('/');
      return;
    }
    const user = JSON.parse(userString);

    if (user.role !== 'doctor') {
      alert("Access Denied. Doctors only.");
      navigate('/');
      return;
    }

    setDoctor(user);
    setMySlots(user.availableSlots || []);

    setEditFormData({
      name: user.name || '',
      phone: user.phone || '',
      specialty: user.specialty || '',
      fee: user.fee || '',
      qualifications: user.qualifications || '',
      experience: user.experience || '',
      image: user.image || ''
    });

    fetchDashboardData(user._id, user.fee);

  }, [navigate]);

  const fetchDashboardData = (doctorId, fee) => {
    // FIX: Using template literals correctly `${...}`
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/bookings/doctor/${doctorId}`)
      .then(res => {
        console.log("Doctor Dashboard API Response:", res.data); // DEBUGGING

        // LAYER 2: Extract array safely. Handles {data: [...]}, {bookings: [...]}, or direct [...]
        let dataList = [];
        if (Array.isArray(res.data)) {
            dataList = res.data;
        } else if (res.data.data && Array.isArray(res.data.data)) {
            dataList = res.data.data;
        } else if (res.data.bookings && Array.isArray(res.data.bookings)) {
            dataList = res.data.bookings;
        }

        setAppointments(dataList);

        // Safe Stats Calculation
        const uniquePatients = new Set(dataList.map(a => a.userId)).size;
        const paidAppts = dataList.filter(a => a.status === 'Confirmed' || a.status === 'Paid').length;
        const pendingAppts = dataList.filter(a => a.status === 'Pending').length;
        const currentFee = fee ? parseFloat(fee) : 0;
        const earnings = paidAppts * currentFee;

        setStats({
          totalAppointments: dataList.length,
          totalPatients: uniquePatients,
          totalEarnings: earnings,
          pendingAppointments: pendingAppts
        });
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching dashboard data:", err);
        setAppointments([]); // Fallback to empty array on error
        setLoading(false);
      });
  };

  const formatDoctorName = (name) => {
    if (!name) return '';
    if (name.toLowerCase().startsWith('dr') || name.toLowerCase().startsWith('prof')) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return `Dr. ${name.charAt(0).toUpperCase() + name.slice(1)}`;
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setEditFormData({ ...editFormData, image: reader.result });
      };
    }
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/update/${doctor._id}`, editFormData)
      .then(res => {
        alert("Profile Updated Successfully!");
        const updatedUser = res.data.user || res.data; 
        localStorage.setItem('DDoctorUser', JSON.stringify(updatedUser));
        setDoctor(updatedUser);
        fetchDashboardData(updatedUser._id, updatedUser.fee);
        setShowEditModal(false);
      })
      .catch(err => alert("Error updating profile"));
  };

  const handleStatusChange = (bookingId, newStatus) => {
    axios.put(`${process.env.REACT_APP_API_BASE_URL}/bookings/update-status/${bookingId}`, { status: newStatus })
      .then(res => {
        setAppointments(prev => prev.map(appt =>
          appt._id === bookingId ? { ...appt, status: newStatus } : appt
        ));
        fetchDashboardData(doctor._id, doctor.fee);
      })
      .catch(err => alert("Failed to update status"));
  };

  const handleAddSlot = () => {
    if (!scheduleDate || !scheduleTimeStart || !scheduleTimeEnd) {
      alert("Please fill date and both times");
      return;
    }
    const newSlot = {
      date: scheduleDate,
      startTime: scheduleTimeStart,
      endTime: scheduleTimeEnd,
      status: 'Available'
    };
    const updatedSlots = [...mySlots, newSlot];
    setMySlots(updatedSlots);
    saveSlotsToBackend(updatedSlots);
    setScheduleTimeStart('');
    setScheduleTimeEnd('');
  };

  const handleRemoveSlot = (index) => {
    const updatedSlots = mySlots.filter((_, i) => i !== index);
    setMySlots(updatedSlots);
    saveSlotsToBackend(updatedSlots);
  };

  const saveSlotsToBackend = (slots) => {
    axios.put(`${process.env.REACT_APP_API_BASE_URL}/users/update/${doctor._id}`, { availableSlots: slots })
      .then(res => {
        const updatedUser = res.data.user || res.data;
        localStorage.setItem('DDoctorUser', JSON.stringify(updatedUser));
      })
      .catch(err => console.error("Error saving slots", err));
  };

  if (loading) return <div className="text-center mt-5 pt-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="container py-5" style={{ marginTop: '20px' }}>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-0">Doctor Dashboard</h2>
          <p className="text-muted">Welcome back, {formatDoctorName(doctor?.name)}</p>
        </div>
        <div>
          <button className="btn btn-primary" onClick={() => setShowEditModal(true)}>
            <i className="bi bi-pencil-square me-2"></i> Edit Profile
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-3 mb-4">
          <div className="card shadow-sm border-0 p-3 text-center bg-white">
            <div className="mb-3 mx-auto rounded-circle overflow-hidden d-flex align-items-center justify-content-center bg-light" style={{ width: '100px', height: '100px' }}>
              {doctor?.image ? (
                <img src={doctor.image} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <i className="bi bi-person-circle fs-1 text-secondary"></i>
              )}
            </div>
            <h5 className="fw-bold text-capitalize">{formatDoctorName(doctor?.name)}</h5>
            <span className="badge bg-primary mb-2">{doctor?.specialty || 'General'}</span>
            <p className="small text-muted mb-2">{doctor?.qualifications}</p>
            <hr />
            <div className="text-start small">
              <p className="mb-1"><i className="bi bi-telephone me-2"></i> {doctor?.phone || 'N/A'}</p>
              <p className="mb-1"><i className="bi bi-envelope me-2"></i> {doctor?.email}</p>
              <p className="mb-1"><i className="bi bi-cash me-2"></i> Fee: <strong>Rs. {doctor?.fee || 0}</strong></p>
            </div>
          </div>
        </div>

        <div className="col-md-9">
          <ul className="nav nav-tabs mb-4 border-bottom-0">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'overview' ? 'active fw-bold' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'schedule' ? 'active fw-bold' : ''}`} onClick={() => setActiveTab('schedule')}>Manage Schedule</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'payments' ? 'active fw-bold' : ''}`} onClick={() => setActiveTab('payments')}>Payments & Fees</button>
            </li>
          </ul>

          {activeTab === 'overview' && (
            <>
              <div className="row mb-4">
                <div className="col-md-4"><div className="card p-3 border-0 shadow-sm bg-primary text-white h-100"><h5>Total Appointments</h5><h2 className="fw-bold">{stats.totalAppointments}</h2></div></div>
                <div className="col-md-4"><div className="card p-3 border-0 shadow-sm bg-success text-white h-100"><h5>Total Patients</h5><h2 className="fw-bold">{stats.totalPatients}</h2></div></div>
                <div className="col-md-4"><div className="card p-3 border-0 shadow-sm bg-warning text-dark h-100"><h5>Pending Requests</h5><h2 className="fw-bold">{stats.pendingAppointments}</h2></div></div>
              </div>
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3"><h5 className="mb-0 fw-bold">Recent Appointments</h5></div>
                <div className="card-body p-0">
                  {/* LAYER 3: Check Array.isArray inside JSX before mapping */}
                  {!Array.isArray(appointments) || appointments.length === 0 ? <p className="text-center p-4 text-muted">No appointments found.</p> : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0 align-middle">
                        <thead className="table-light"><tr><th>Patient</th><th>Date/Time</th><th>Status</th><th>Action</th></tr></thead>
                        <tbody>
                          {appointments.map(appt => (
                            <tr key={appt._id}>
                              <td><div className="fw-bold">{appt.patientName}</div><small className="text-muted">{appt.contactPhone}</small></td>
                              <td><div>{appt.appointmentDate}</div><small className="text-muted">{appt.appointmentTime}</small></td>
                              <td><span className={`badge ${appt.status === 'Confirmed' ? 'bg-success' : appt.status === 'Cancelled' ? 'bg-danger' : 'bg-warning text-dark'}`}>{appt.status}</span></td>
                              <td>
                                {appt.status === 'Pending' && (
                                  <div className="d-flex gap-2">
                                    <button className="btn btn-sm btn-success" onClick={() => handleStatusChange(appt._id, 'Confirmed')}><i className="bi bi-check-lg"></i></button>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleStatusChange(appt._id, 'Cancelled')}><i className="bi bi-x-lg"></i></button>
                                  </div>
                                )}
                                {appt.status === 'Confirmed' && <span className="text-success small fw-bold">Accepted</span>}
                                {appt.status === 'Cancelled' && <span className="text-danger small fw-bold">Rejected</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {activeTab === 'schedule' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="fw-bold mb-4">Set Your Availability</h5>
                <div className="row g-3 align-items-end mb-4 bg-light p-3 rounded">
                  <div className="col-md-4"><label className="form-label small fw-bold">Select Date</label><input type="date" className="form-control" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label small fw-bold">Start Time</label><input type="time" className="form-control" value={scheduleTimeStart} onChange={(e) => setScheduleTimeStart(e.target.value)} /></div>
                  <div className="col-md-3"><label className="form-label small fw-bold">End Time</label><input type="time" className="form-control" value={scheduleTimeEnd} onChange={(e) => setScheduleTimeEnd(e.target.value)} /></div>
                  <div className="col-md-2"><button className="btn btn-success w-100" onClick={handleAddSlot}><i className="bi bi-plus-lg"></i> Add</button></div>
                </div>
                <h6 className="fw-bold text-muted">Current Available Slots</h6>
                {mySlots.length === 0 ? <p className="text-muted small">No slots added yet.</p> : (
                  <div className="table-responsive">
                    <table className="table table-bordered text-center">
                      <thead className="table-light"><tr><th>Date</th><th>Time Slot</th><th>Status</th><th>Action</th></tr></thead>
                      <tbody>
                        {mySlots.map((slot, index) => (
                          <tr key={index}>
                            <td>{slot.date}</td>
                            <td>{slot.startTime} - {slot.endTime}</td>
                            <td><span className={`badge ${slot.status === 'Booked' ? 'bg-danger' : 'bg-success'}`}>{slot.status}</span></td>
                            <td><button className="btn btn-sm btn-outline-danger" onClick={() => handleRemoveSlot(index)}><i className="bi bi-trash"></i></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          {activeTab === 'payments' && (
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="fw-bold mb-0">Financial Report</h5>
                  <div className="bg-success text-white px-4 py-2 rounded"><small>Total Estimated Earnings</small><h3 className="mb-0 fw-bold">Rs. {stats.totalEarnings.toLocaleString()}</h3></div>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead><tr><th>Date</th><th>Patient Name</th><th>Consultation Fee</th><th>Payment Status</th></tr></thead>
                    <tbody>
                      {/* LAYER 3 REPEATED: Safety Check */}
                      {Array.isArray(appointments) && appointments.map((appt) => (
                        <tr key={appt._id}>
                          <td>{appt.appointmentDate} <br /><small className="text-muted">{appt.appointmentTime}</small></td>
                          <td>{appt.patientName}</td>
                          <td>Rs. {doctor?.fee || 0}</td>
                          <td>{appt.status === 'Paid' || appt.status === 'Confirmed' ? <span className="text-success fw-bold"><i className="bi bi-check-circle-fill"></i> {appt.status}</span> : <span className="text-warning fw-bold"><i className="bi bi-hourglass-split"></i> {appt.status}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Profile</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <form onSubmit={handleUpdateProfile}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name</label>
                      <input type="text" className="form-control" value={editFormData.name} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} required />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Phone</label>
                      <input type="text" className="form-control" value={editFormData.phone} onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Specialty</label>
                      <input type="text" className="form-control" value={editFormData.specialty} onChange={(e) => setEditFormData({ ...editFormData, specialty: e.target.value })} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Consultation Fee</label>
                      <input type="number" className="form-control" value={editFormData.fee} onChange={(e) => setEditFormData({ ...editFormData, fee: e.target.value })} />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Qualifications & Experience</label>
                      <input type="text" className="form-control mb-2" placeholder="Qualifications" value={editFormData.qualifications} onChange={(e) => setEditFormData({ ...editFormData, qualifications: e.target.value })} />
                      <input type="text" className="form-control" placeholder="Experience" value={editFormData.experience} onChange={(e) => setEditFormData({ ...editFormData, experience: e.target.value })} />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Profile Picture</label>
                      <input type="file" className="form-control" accept="image/*" onChange={handleImageUpload} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DoctorDashboard;